import React, { useContext, useRef, useEffect, useState } from "react";
import { Box, Container, Typography, useMediaQuery, useTheme, Button, Tooltip } from '@mui/material';
import { BusinessContext } from '../context/BusinessContext';
import { ReviewsContext } from '../context/ReviewsContext';
import ReviewsTable from '../components/ReviewsTable';
import ReviewsOverview from '../components/ReviewsOverview';
import { useRatingChartToggle, RatingChartToggle } from '../components/RatingChartUtils';
import CumulativeRatingChart from '../components/CumulativeRatingChart';
import MonthlyAverageRatingChart from '../components/MonthlyAverageRatingChart';
import RateReviewIcon from '@mui/icons-material/RateReview';
import TimelineIcon from '@mui/icons-material/Timeline';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router-dom';

const Reviews = () => {
  const { currencySymbol, selectedBusiness } = useContext(BusinessContext);
  const { reviewsData, view, showColoredText, selectedDate, setSelectedDate } = useContext(ReviewsContext);
  const theme = useTheme();
  const isLandscape = useMediaQuery('(orientation: landscape) and (max-width: 900px)');
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [signupDate, setSignupDate] = useState(null);
  const formattedDate = signupDate ? signupDate.toLocaleDateString() : '';
  const navigate = useNavigate();
  const [subscriptionActive, setSubscriptionActive] = useState(null);

  const toggleContainerRef = useRef(null);
  const chartsContainerRef = useRef(null);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        if (response.ok) {
          const userData = await response.json();
          if (userData.created_at) {
            setSignupDate(new Date(userData.created_at));
          }
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };
    
    fetchUserInfo();
  }, []);

  const { viewMode, handleViewModeChange } = useRatingChartToggle();
  
  const handleChartViewModeChange = (event, newMode) => {
    setSelectedDate(null);
    handleViewModeChange(event, newMode);
  };

  const filteredReviews = React.useMemo(() => {
    if (!selectedDate || !reviewsData) return reviewsData;
    
    return reviewsData.filter(review => {
      const reviewDate = new Date(review.estimated_datetime);
      return (
        reviewDate.getFullYear() === selectedDate.getFullYear() &&
        reviewDate.getMonth() === selectedDate.getMonth()
      );
    });
  }, [reviewsData, selectedDate]);

  useEffect(() => {
    if (!selectedDate) return;

    const handlePageClick = (event) => {
      if (toggleContainerRef.current && toggleContainerRef.current.contains(event.target)) {
        return;
      }
      if (chartsContainerRef.current && chartsContainerRef.current.contains(event.target)) {
        return;
      }
      setSelectedDate(null);
    };

    document.addEventListener('click', handlePageClick);

    return () => {
      document.removeEventListener('click', handlePageClick);
    };
  }, [selectedDate, setSelectedDate]);

  const handleUnlockOngoingAnalysis = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/payment-checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ plan: 'monthly' }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const { checkout_url } = await response.json();
      window.location.href = checkout_url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
    }
  };

  return (
    <Box 
      sx={{
        minHeight: 'auto',
        background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
        display: 'flex',
        flexDirection: 'column',
        py: { 
          xs: 2,
          lg: 4 
        },
        px: { 
          xs: 1,
          lg: 4 
        },
        opacity: 0,
        animation: 'fadeIn 1s forwards',
        '@keyframes fadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      <Container 
        maxWidth="xl"
        sx={{
          width: '100%',
          pb: { 
            xs: 6,
            md: 8,
            lg: 10 
          }
        }}
      >
        {/* Mobile Premium Banner removed - now only on Businesses page */}
        
        {/* Header Section with 3-way split */}
        <Box sx={{ 
          mb: { 
            xs: 2,
            lg: 4 
          },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 3 },
          alignItems: { xs: 'flex-start', md: 'flex-start' },
          width: '100%',
          height: { xs: 'auto', md: '280px' }
        }}>
          {/* Title Section - 1/3 width */}
          <Box sx={{ 
            flex: { xs: '1 1 100%', md: '1 1 33.33%' },
            px: { xs: 0, md: 2 },
            height: { xs: 'auto', md: '100%' }
          }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 800,
                fontSize: { 
                  xs: '1.75rem',
                  lg: '2.5rem'
                },
                color: '#0A2540',
                letterSpacing: '-0.02em',
                mb: { 
                  xs: 1,
                  lg: 2 
                },
                display: 'flex',
                alignItems: 'center',
                gap: { 
                  xs: 1,
                  lg: 2 
                }
              }}
            >
              <RateReviewIcon sx={{ 
                fontSize: 'inherit',
                color: '#1976d2'
              }} />
              Reviews
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#546E7A',
                fontSize: { 
                  xs: '0.95rem',
                  lg: '1.1rem'
                }
              }}
            >
              Analyze your customer reviews and their impact on your business
            </Typography>
          </Box>
          
          {/* Reviews Overview Section - 1/3 width */}
          <Box sx={{ 
            flex: { xs: '1 1 100%', md: '1 1 33.33%' },
            px: { xs: 0, md: 2 },
            height: { xs: 'auto', md: '100%' }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography 
                variant="h6" 
                fontWeight="bold"
                sx={{ 
                  borderBottom: '2px solid #1976d2',
                  pb: 0.5,
                  display: 'inline-block',
                  fontSize: '1.25rem'
                }}
              >
                Quick Overview
              </Typography>
            </Box>
            <Box sx={{ height: { xs: 'auto', md: 'calc(100% - 40px)' }, overflow: 'auto' }}>
              <ReviewsOverview />
            </Box>
          </Box>
          
          {/* Rating Chart Section - 1/3 width */}
          <Box sx={{ 
            flex: { xs: '1 1 100%', md: '1 1 33.33%' },
            px: { xs: 0, md: 2 },
            height: { xs: 'auto', md: '100%' },
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TimelineIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography 
                variant="h6" 
                fontWeight="bold"
                sx={{ 
                  borderBottom: '2px solid #1976d2',
                  pb: 0.5,
                  display: 'inline-block',
                  fontSize: '1.25rem'
                }}
              >
                Rating History
              </Typography>
              <Box ref={toggleContainerRef} sx={{ ml: 'auto' }}>
                <RatingChartToggle
                  viewMode={viewMode}
                  handleViewModeChange={handleChartViewModeChange}
                />
              </Box>
            </Box>
            <Box 
              ref={chartsContainerRef}
              sx={{ 
                flex: 1,
                height: { xs: 'auto', md: 'calc(100% - 40px)' },
                minHeight: { xs: '250px', md: 'auto' },
                maxHeight: { xs: '350px', md: 'calc(100% - 40px)' },
                display: 'flex',
                position: 'relative',
                overflow: 'hidden',
                width: '100%',
                '& svg': {
                  overflow: 'hidden',
                  maxHeight: '350px !important' 
                },
                '& .MuiAreaElement-series-ratingTrend': {
                  display: 'none'
                }
              }}
            >
              {viewMode === 'cumulative' ? (
                <CumulativeRatingChart />
              ) : (
                <MonthlyAverageRatingChart />
              )}
            </Box>
          </Box>
        </Box>

        <ReviewsTable
          reviewsData={filteredReviews}
          currencySymbol={currencySymbol}
          view={view}
          selectedBusiness={selectedBusiness}
          showColoredText={showColoredText}
        />
      </Container>
    </Box>
  );
};

export default Reviews;
